import {
  type QueryFunctionContext,
  type QueryKey,
} from '@tanstack/react-query';
import type {
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';

import {
  // useAuthFetchRaw,
  useAuthFetch,
} from '#src/hooks/UseAuthFetch';

type MetaKeys = 'sorting' | 'filters';
type MetaValues = SortingState | ColumnFiltersState;
export type DefaultQueryMeta = Record<MetaKeys, MetaValues>;

export interface QueryFnArgs {
  queryKey: string[];
}
/**
 * Default query function for use with QueryClient.
 * String values in the Query key array are used to generate a path relative to the page url.
 * 'pageParam' is optionally used for pagination.
 * 'meta' may be used to specify search parameters.
 *
 * @export
 * @template T
 * @template TQueryKey
 * @template TPageParam
 * @param {QueryFunctionContext<TQueryKey, TPageParam>} context
 * @return {*}  {Promise<T>}
 */
export async function defaultQueryFn<
  T = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  context: QueryFunctionContext<TQueryKey, TPageParam>,
): Promise<T> {
  const {
    queryKey,
    meta,
    pageParam,
  } = context;
  const queryKeyStrParts = queryKey.filter(part => (typeof part === 'string' || part instanceof String));
  const apiPath = queryKeyStrParts.join('/');
  const url = new URL(`${window.origin}/api/${apiPath}`);
  let pageNumber = 0;
  if (typeof pageParam === 'string') {
    pageNumber = parseInt(pageParam);
  }
  const searchParams: Record<string, string> = pageNumber
    ? {
        start: pageNumber.toString(),
      }
    : {};
  url.search = new URLSearchParams(searchParams).toString();
  const res = await useAuthFetch<T>(
    url,
    {
      method: 'GET',
    },
  );
  const {
    data,
  } = res;
  return data;
}
